<script setup lang="ts">
import { bitable } from '@lark-base-open/js-sdk'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Search, X, Check, Copy, Link2, List, Settings, ChevronDown
} from 'lucide-vue-next'
import { toast } from '@/utils/toast'
import NestedFieldVariable from '@/components/NestedFieldVariable.vue'
import {
  loadAllFields,
  filterFieldsByQuery
} from '@/utils/fieldManager'
import type { Template, FieldMetadata } from '@/types'

interface Props {
  activeTemplate: Template | null
  baseToken: string
  tableId: string
}

defineProps<Props>()

const { t } = useI18n()

// State
const allFields = ref<FieldMetadata[]>([])
const copiedVariable = ref<string | null>(null)
const expandedFields = ref<Record<string, boolean>>({})
const searchQuery = ref('')
const unsubscribeFieldAdd = ref<(() => void) | null>(null)
const unsubscribeFieldDelete = ref<(() => void) | null>(null)
const unsubscribeFieldModify = ref<(() => void) | null>(null)

// Shared debounce timer for all field change events
let fieldChangeDebounce: number | null = null

// Computed
const filteredFields = computed(() => {
  return filterFieldsByQuery(allFields.value, searchQuery.value)
})

// Variable operations
const toggleFieldExpand = (fieldKey: string) => {
  expandedFields.value[fieldKey] = !expandedFields.value[fieldKey]
}

const copyVariable = (variable: string) => {
  const textToCopy = `{${variable}}`
  const textArea = document.createElement('textarea')
  textArea.value = textToCopy
  textArea.style.position = 'fixed'
  textArea.style.left = '-999999px'
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    document.execCommand('copy')
    copiedVariable.value = variable
    setTimeout(() => {
      copiedVariable.value = null
    }, 2000)
    toast.success(t('copied'))
  } catch (err) {
    console.error('Failed to copy:', err)
  } finally {
    document.body.removeChild(textArea)
  }
}

// Load all fields
const loadFields = async () => {
  try {
    const selection = await bitable.base.getSelection()
    if (selection.tableId) {
      // Load all fields with depth 5 for nested links, including system vars
      allFields.value = await loadAllFields(selection.tableId, 5, true)
    }
  } catch (error) {
    console.error('Failed to load field variables:', error)
  }
}

// Setup field change listeners
const setupFieldListeners = async () => {
  try {
    const selection = await bitable.base.getSelection()
    if (!selection.tableId) return

    const table = await bitable.base.getTableById(selection.tableId)

    // All field change events share a single debounce timer
    // Silent updates - no toasts since TemplateConfig already shows them

    unsubscribeFieldAdd.value = table.onFieldAdd(async () => {
      if (fieldChangeDebounce) clearTimeout(fieldChangeDebounce)
      fieldChangeDebounce = window.setTimeout(async () => {
        await loadFields()
        fieldChangeDebounce = null
      }, 100)
    })

    unsubscribeFieldDelete.value = table.onFieldDelete(async () => {
      if (fieldChangeDebounce) clearTimeout(fieldChangeDebounce)
      fieldChangeDebounce = window.setTimeout(async () => {
        await loadFields()
        fieldChangeDebounce = null
      }, 100)
    })

    unsubscribeFieldModify.value = table.onFieldModify(async () => {
      if (fieldChangeDebounce) clearTimeout(fieldChangeDebounce)
      fieldChangeDebounce = window.setTimeout(async () => {
        await loadFields()
        fieldChangeDebounce = null
      }, 100)
    })
  } catch (error) {
    console.error('Failed to setup field listeners:', error)
  }
}

// Cleanup listeners
const cleanupListeners = () => {
  // Clear pending debounce timer
  if (fieldChangeDebounce) {
    clearTimeout(fieldChangeDebounce)
    fieldChangeDebounce = null
  }

  // Unsubscribe from events
  if (unsubscribeFieldAdd.value) {
    unsubscribeFieldAdd.value()
    unsubscribeFieldAdd.value = null
  }
  if (unsubscribeFieldDelete.value) {
    unsubscribeFieldDelete.value()
    unsubscribeFieldDelete.value = null
  }
  if (unsubscribeFieldModify.value) {
    unsubscribeFieldModify.value()
    unsubscribeFieldModify.value = null
  }
}

onMounted(async () => {
  await loadFields()
  await setupFieldListeners()
})

onUnmounted(() => {
  cleanupListeners()
})
</script>

<template>
  <div class="h-full bg-white flex flex-col overflow-hidden">
    <div class="flex-1 overflow-y-auto">
      <div class="max-w-4xl mx-auto w-full p-8">
        <div class="space-y-6">
          <div>
            <h1 class="text-2xl font-bold text-gray-800 mb-2">{{ t('variables') }}</h1>
            <p class="text-sm text-gray-600">{{ t('copyToClipboard') }}</p>
          </div>

          <!-- Search -->
          <div class="relative">
            <Search :size="16" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('searchVariables')"
              class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X :size="16" />
            </button>
          </div>

          <!-- All Fields -->
          <div class="flex flex-col gap-2">
            <template v-for="field in filteredFields" :key="field.variable + '_' + field.depth">
              <!-- Multi-link field (loop) -->
              <div v-if="field.isLinked && field.isMultiple">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-purple-50 border-l-2 border-l-purple-400 rounded-md cursor-pointer hover:bg-purple-100"
                  @click="toggleFieldExpand(field.variable + '_' + field.depth)"
                >
                  <component :is="ChevronDown" :class="['w-4 h-4 text-gray-600 transition-transform', !expandedFields[field.variable + '_' + field.depth] && '-rotate-90']" />
                  <component :is="List" class="w-4 h-4 text-purple-600" />
                  <span class="text-sm text-gray-700 flex-1">{{ field.name }}</span>
                  <span class="text-xs text-purple-600 bg-purple-200 px-2 py-0.5 rounded">Loop</span>
                </div>

                <div v-show="expandedFields[field.variable + '_' + field.depth]" class="ml-4 mt-2 flex flex-col gap-2 mb-2">
                  <!-- Loop start -->
                  <div
                    class="flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-600"
                    @click="copyVariable('#' + field.variable)"
                  >
                    <span class="text-sm text-blue-600 font-mono flex-1">{{ '{#' + field.variable + '}' }}</span>
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded">Start</span>
                      <component :is="Check" v-if="copiedVariable === '#' + field.variable" class="w-4 h-4 text-green-600" />
                      <component :is="Copy" v-else class="w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  <!-- Nested fields -->
                  <nested-field-variable
                    v-for="nested in field.nestedVariables"
                    :key="nested.variable"
                    :field="nested"
                    :copied-variable="copiedVariable"
                    :expanded-fields="expandedFields"
                    @copy="copyVariable"
                    @toggle="toggleFieldExpand"
                  />

                  <!-- Loop end -->
                  <div
                    class="flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-600"
                    @click="copyVariable('/' + field.variable)"
                  >
                    <span class="text-sm text-blue-600 font-mono flex-1">{{ '{/' + field.variable + '}' }}</span>
                    <div class="flex items-center gap-2">
                      <span class="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded">End</span>
                      <component :is="Check" v-if="copiedVariable === '/' + field.variable" class="w-4 h-4 text-green-600" />
                      <component :is="Copy" v-else class="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Single-link field (nested) -->
              <div v-else-if="field.isLinked && !field.isMultiple">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-blue-50 border-l-2 border-l-blue-400 rounded-md cursor-pointer hover:bg-blue-100"
                  @click="toggleFieldExpand(field.variable + '_' + field.depth)"
                >
                  <component :is="ChevronDown" :class="['w-4 h-4 text-gray-600 transition-transform', !expandedFields[field.variable + '_' + field.depth] && '-rotate-90']" />
                  <component :is="Link2" class="w-4 h-4 text-blue-600" />
                  <span class="text-sm text-gray-700 flex-1">{{ field.name }}</span>
                </div>

                <div v-show="expandedFields[field.variable + '_' + field.depth]" class="ml-4 mt-2 flex flex-col gap-2 mb-2">
                  <nested-field-variable
                    v-for="nested in field.nestedVariables"
                    :key="nested.variable"
                    :field="nested"
                    :copied-variable="copiedVariable"
                    :expanded-fields="expandedFields"
                    @copy="copyVariable"
                    @toggle="toggleFieldExpand"
                  />
                </div>
              </div>

              <!-- System field -->
              <div
                v-else-if="field.isSystem"
                class="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-600"
                @click="copyVariable(field.variable)"
              >
                <component :is="Settings" class="w-4 h-4 text-gray-600" />
                <span class="text-sm text-blue-600 font-mono flex-1">{{ '{' + field.variable + '}' }}</span>
                <component :is="Check" v-if="copiedVariable === field.variable" class="w-4 h-4 text-green-600" />
                <component :is="Copy" v-else class="w-4 h-4 text-gray-400" />
              </div>

              <!-- Regular field -->
              <div
                v-else
                class="flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-600"
                @click="copyVariable(field.variable)"
              >
                <span class="text-sm text-blue-600 font-mono flex-1">{{ '{' + field.variable + '}' }}</span>
                <component :is="Check" v-if="copiedVariable === field.variable" class="w-4 h-4 text-green-600" />
                <component :is="Copy" v-else class="w-4 h-4 text-gray-400" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
